import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import PostCard from "@/components/PostCard";
import { MediaFrame, ProfilePostModal } from "@/components/ProfilePost";
import { toast, Toaster } from "sonner";
import { History, Archive, Bookmark, BookmarkX, Images } from "lucide-react";

/** Activity & Saved — owner-only. "My Posts" is the Wall archive; "Saved" is
 *  Client Profile Posts this Client bookmarked (pinned Pros live on My Board). */
export default function MyActivity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "saved" ? "saved" : "posts";
  const [posts, setPosts] = useState([]);
  const [saved, setSaved] = useState([]);
  const [cfg, setCfg] = useState({ post_reactions: [], comment_chips: { praise: [], intent: [] } });
  const [loading, setLoading] = useState(true);
  const [openPost, setOpenPost] = useState(null);

  const load = () => api.get("/wall/my-posts")
    .then(r => setPosts(r.data.posts || []))
    .catch(() => setPosts([]))
    .finally(() => setLoading(false));

  const loadSaved = () => api.get("/profile-posts/saved")
    .then(r => setSaved(r.data.posts || []))
    .catch(() => setSaved([]));

  useEffect(() => {
    if (user === null) return;          // still restoring the session
    if (!user) { navigate("/login"); return; }
    load();
    loadSaved();
    api.get("/config/public").then(r => setCfg({
      post_reactions: r.data.post_reactions || [],
      comment_chips: r.data.comment_chips || { praise: [], intent: [] },
    })).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const unsave = async (postId) => {
    try {
      await api.post(`/profile-posts/${postId}/save`);
      setSaved(s => s.filter(p => p.id !== postId));
      toast.success("Removed from saved");
    } catch { toast.error("Could not remove that"); }
  };

  const active = posts.filter(p => p.on_wall);
  const archived = posts.filter(p => !p.on_wall);

  const Section = ({ title, note, rows, testid }) => (
    <section className="mb-10" data-testid={testid}>
      <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-[#222321]/45 mb-1">{title}</h2>
      <p className="text-xs text-[#222321]/45 mb-4">{note}</p>
      <div className="space-y-5">
        {rows.map(p => (
          <PostCard key={p.id} post={p} author={{ user_id: p.user_id, name: user?.name, avatar_url: user?.avatar_url, kind: "client" }}
            reactions={cfg.post_reactions} chips={cfg.comment_chips} editable onChanged={load}
            theme={{ card: "#FFFFFF", pin: "#C79B6B" }}/>
        ))}
      </div>
    </section>
  );

  const TabBtn = ({ id, label, count, Icon }) => (
    <button
      onClick={() => setParams(id === "posts" ? {} : { tab: id })}
      className={`px-4 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 transition border ${
        tab === id ? "bg-[#245544] text-white border-transparent" : "bg-white border-[#D3DAD5] text-[#222321] hover:border-[#2E6B57]"
      }`}
      data-testid={`activity-tab-${id}`}
    >
      <Icon size={15}/> {label}
      {count > 0 && <span className={`text-[11px] font-bold ${tab === id ? "text-white/70" : "text-[#222321]/40"}`}>{count}</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F5F7F5]">
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#222321] flex items-center gap-2" style={{ fontFamily: "Outfit" }} data-testid="my-activity-title">
            <History size={24} className="text-[#2E6B57]"/> Activity &amp; Saved
          </h1>
          <p className="text-sm text-[#222321]/60 mt-1">Your Wall history and the neighbor posts you bookmarked. Only you can see this page.</p>
        </div>

        <div className="flex items-center gap-2.5 mb-8" data-testid="activity-tabs">
          <TabBtn id="posts" label="My Posts" count={posts.length} Icon={History}/>
          <TabBtn id="saved" label="Saved" count={saved.length} Icon={Bookmark}/>
        </div>

        {tab === "posts" && (loading ? (
          <p className="text-sm text-[#222321]/40 py-10 text-center">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-[#D3DAD5] rounded-2xl p-10 text-center" data-testid="my-activity-empty">
            <div className="w-12 h-12 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-4">
              <Archive size={20} className="text-[#245544]"/>
            </div>
            <h2 className="text-lg font-bold text-[#222321] mb-1" style={{ fontFamily: "Outfit" }}>No Wall posts yet</h2>
            <p className="text-sm text-[#222321]/55 mb-5">Share a project or recommend a pro to your neighborhood and it'll be archived here.</p>
            <button onClick={() => navigate("/feed?tab=wall")} className="px-5 py-2.5 text-sm font-semibold rounded-full bg-[#245544] text-white" data-testid="my-activity-go-wall">Go to the Wall</button>
          </div>
        ) : (
          <>
            {active.length > 0 && <Section title="On the Wall now" note="Currently visible to neighbors nearby." rows={active} testid="my-activity-active"/>}
            {archived.length > 0 && <Section title="Archived" note="No longer shown on the active Wall — kept here until you delete it." rows={archived} testid="my-activity-archived"/>}
          </>
        ))}

        {tab === "saved" && (saved.length === 0 ? (
          <div className="bg-white border border-[#D3DAD5] rounded-2xl p-10 text-center" data-testid="saved-empty">
            <div className="w-12 h-12 rounded-full bg-[#E9F0EC] grid place-items-center mx-auto mb-4">
              <Bookmark size={20} className="text-[#245544]"/>
            </div>
            <h2 className="text-lg font-bold text-[#222321] mb-1" style={{ fontFamily: "Outfit" }}>Nothing saved yet</h2>
            <p className="text-sm text-[#222321]/55">Tap Save on a neighbor's post and it'll be kept here for later. Professionals you pin live on My Board.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="saved-list">
            {saved.map(p => (
              <article key={p.id} className="bg-white border border-[#D3DAD5] rounded-2xl overflow-hidden flex" data-testid={`saved-post-${p.id}`}>
                <button onClick={() => setOpenPost(p.id)} className="w-28 sm:w-36 shrink-0 relative" data-testid={`saved-post-open-${p.id}`}>
                  <MediaFrame item={p.media?.[0]} rounded="rounded-none" className="w-full h-full min-h-[112px] object-cover"/>
                  {p.media?.length > 1 && (
                    <span className="absolute bottom-1.5 right-1.5 text-[10px] font-semibold text-white bg-black/50 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      <Images size={10}/> {p.media.length}
                    </span>
                  )}
                </button>
                <div className="flex-1 min-w-0 p-3.5 flex flex-col">
                  <button onClick={() => navigate(`/client/${p.user_id}`)} className="text-sm font-bold text-[#222321] text-left hover:underline truncate" data-testid={`saved-post-author-${p.id}`}>
                    {p.author?.name}
                  </button>
                  {p.category && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#2E6B57] mt-0.5">{p.category}</span>}
                  {p.caption && <p className="text-[13px] text-[#222321]/75 leading-snug mt-1 line-clamp-2">{p.caption}</p>}
                  <div className="flex items-center gap-2 mt-auto pt-2.5">
                    <button onClick={() => setOpenPost(p.id)} className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[#EEF2EF] text-[#245544] hover:bg-[#E3EAE5]" data-testid={`saved-post-view-${p.id}`}>
                      Open post
                    </button>
                    <button onClick={() => unsave(p.id)} className="px-3 py-1.5 text-xs font-semibold rounded-full border border-[#D3DAD5] text-[#222321]/70 hover:border-red-300 hover:text-red-600 flex items-center gap-1.5" data-testid={`saved-post-unsave-${p.id}`}>
                      <BookmarkX size={13}/> Unsave
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ))}
      </div>

      {openPost && (
        <ProfilePostModal postId={openPost} onClose={() => { setOpenPost(null); loadSaved(); }} onChanged={loadSaved}/>
      )}
    </div>
  );
}
